import { useEffect, useState } from 'react';
import {
  ArrowsLeftRight, Warning, CheckCircle, ArrowsClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const MODE_OPTIONS = [
  { value: 'all', label: 'Alle data', desc: 'Verschuif álles van bron naar doel' },
  { value: 'countries', label: 'Per land', desc: 'Alleen facilities in geselecteerde landen + gekoppelde data' },
  { value: 'facility_types', label: 'Per type', desc: 'Alleen specifieke facility types (gemeentelijk, hotel, etc.)' },
];

export default function ProfileMovePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromProfile, setFromProfile] = useState('');
  const [toProfile, setToProfile] = useState('');
  const [mode, setMode] = useState('all');
  const [selCountries, setSelCountries] = useState([]);
  const [selTypes, setSelTypes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [executing, setExecuting] = useState(false);
  const [autoBackup, setAutoBackup] = useState(true);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/api/admin/profile-move/options`, { credentials: 'include' }),
        fetch(`${API}/api/admin/profile-move/log`, { credentials: 'include' }),
      ]);
      if (!r1.ok) throw new Error('Geen toegang of geen data');
      const d = await r1.json();
      setData(d);
      const profiles = d.profiles || [];
      if (profiles.length >= 2) {
        setFromProfile((cur) => cur || profiles[0].slug);
        setToProfile((cur) => cur || profiles[1].slug);
      }
      if (r2.ok) {
        const h = await r2.json();
        setHistory(h.history || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const buildFilters = () => {
    if (mode === 'all') return { mode: 'all' };
    if (mode === 'countries') return { mode: 'countries', countries: selCountries };
    if (mode === 'facility_types') return { mode: 'facility_types', facility_types: selTypes };
    return { mode };
  };

  const runPreview = async () => {
    if (!fromProfile || !toProfile || fromProfile === toProfile) {
      setError('Kies verschillende bron- en doel-profielen');
      return;
    }
    setError(null); setSuccess(null); setPreview(null); setPreviewing(true);
    try {
      const r = await fetch(`${API}/api/admin/profile-move/preview`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_profile: fromProfile, to_profile: toProfile, filters: buildFilters() }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Preview mislukt');
      }
      setPreview(await r.json());
    } catch (e) {
      setError(`Preview mislukt: ${e.message}`);
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    if (confirmText !== 'MOVE') return;
    setError(null); setSuccess(null); setExecuting(true);
    try {
      const r = await fetch(`${API}/api/admin/profile-move/execute`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_profile: fromProfile, to_profile: toProfile,
          filters: buildFilters(), confirm: 'MOVE',
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Move mislukt');
      }
      const res = await r.json();
      setSuccess(`Verplaatst: ${res.total_docs} documenten in ${res.duration_ms}ms`);
      setPreview(null); setConfirmText('');
      await load();
    } catch (e) {
      setError(`Move mislukt: ${e.message}`);
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-zinc-500">
        <ArrowsClockwise size={18} className="animate-spin" /> Laden...
      </div>
    );
  }

  if (!data || !data.profiles || data.profiles.length < 2) {
    return (
      <div className="p-8 max-w-2xl">
        <h1 className="text-xl font-semibold text-zinc-900 mb-2">Data verplaatsen</h1>
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900">
          Je hebt minstens 2 profielen nodig om data te verplaatsen.
          Maak eerst een tweede profiel aan via <strong>Profielen</strong>.
        </div>
      </div>
    );
  }

  const profiles = data.profiles;
  const fromOpts = data.options?.[fromProfile] || {};
  const toOpts = data.options?.[toProfile] || {};

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="profile-move-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 flex items-center gap-2">
          <ArrowsLeftRight size={24} weight="duotone" />
          Data verplaatsen tussen profielen
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Verschuif facilities, contacts, orders, e-mails en gekoppelde data van het ene profiel
          naar het andere. Werkt selectief op land of facility-type.
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2 text-sm text-red-800">
          <Warning size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded flex items-start gap-2 text-sm text-emerald-800">
          <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Profile selectors */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-end">
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Bron-profiel</label>
            <select
              value={fromProfile}
              onChange={(e) => { setFromProfile(e.target.value); setPreview(null); setSelCountries([]); setSelTypes([]); }}
              className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm"
              data-testid="move-from-select"
            >
              {profiles.map(p => (<option key={p.slug} value={p.slug}>{p.name}</option>))}
            </select>
            <div className="text-xs text-zinc-400 mt-1">{fromOpts.total_facilities ?? 0} facilities</div>
          </div>
          <div className="text-3xl text-zinc-400 mx-auto px-2 select-none">→</div>
          <div>
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Doel-profiel</label>
            <select
              value={toProfile}
              onChange={(e) => { setToProfile(e.target.value); setPreview(null); }}
              className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm"
              data-testid="move-to-select"
            >
              {profiles.map(p => (<option key={p.slug} value={p.slug}>{p.name}</option>))}
            </select>
            <div className="text-xs text-zinc-400 mt-1">{toOpts.total_facilities ?? 0} facilities (huidig)</div>
          </div>
        </div>

        {/* Mode selection */}
        <div className="mt-6">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Selectie</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {MODE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => { setMode(opt.value); setPreview(null); }}
                className={`text-left px-3 py-2.5 border rounded-sm text-sm transition-colors ${
                  mode === opt.value
                    ? 'bg-blue-50 border-blue-400 text-blue-900'
                    : 'bg-white border-zinc-200 hover:border-zinc-300 text-zinc-700'
                }`}
                data-testid={`move-mode-${opt.value}`}
              >
                <div className="font-medium">{opt.label}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{opt.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Country selection */}
        {mode === 'countries' && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
              Selecteer landen ({selCountries.length} geselecteerd)
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2 border border-zinc-200 rounded-sm">
              {(fromOpts.countries || []).map(c => {
                const sel = selCountries.includes(c.code);
                return (
                  <button
                    key={c.code}
                    onClick={() => setSelCountries(sel ? selCountries.filter(x => x !== c.code) : [...selCountries, c.code])}
                    className={`px-2 py-1 text-xs rounded-sm border transition-colors ${
                      sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-400'
                    }`}
                    data-testid={`country-chip-${c.code}`}
                  >
                    {c.code} <span className="opacity-60">({c.count})</span>
                  </button>
                );
              })}
              {(fromOpts.countries || []).length === 0 && (
                <span className="text-xs text-zinc-400 italic">Geen landen in bron-profiel</span>
              )}
            </div>
          </div>
        )}

        {/* Type selection */}
        {mode === 'facility_types' && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">
              Selecteer types ({selTypes.length} geselecteerd)
            </div>
            <div className="flex flex-wrap gap-1.5 p-2 border border-zinc-200 rounded-sm">
              {(fromOpts.facility_types || []).map(t => {
                const sel = selTypes.includes(t.type);
                return (
                  <button
                    key={t.type}
                    onClick={() => setSelTypes(sel ? selTypes.filter(x => x !== t.type) : [...selTypes, t.type])}
                    className={`px-2 py-1 text-xs rounded-sm border transition-colors ${
                      sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-zinc-700 border-zinc-300 hover:border-blue-400'
                    }`}
                    data-testid={`type-chip-${t.type}`}
                  >
                    {t.type} <span className="opacity-60">({t.count})</span>
                  </button>
                );
              })}
              {(fromOpts.facility_types || []).length === 0 && (
                <span className="text-xs text-zinc-400 italic">Geen types in bron-profiel</span>
              )}
            </div>
          </div>
        )}

        {/* Preview button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={runPreview}
            disabled={previewing || fromProfile === toProfile}
            className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-2"
            data-testid="move-preview-btn"
          >
            {previewing ? <ArrowsClockwise size={14} className="animate-spin" /> : null}
            Preview verplaatsing
          </button>
        </div>
      </div>

      {/* Preview result */}
      {preview && (
        <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6" data-testid="move-preview">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium text-zinc-900">
              Preview: {preview._total_docs?.toLocaleString('nl-NL') || 0} documenten worden verplaatst
            </h2>
            {preview._facility_ids_count !== undefined && preview._facility_ids_count !== null && (
              <span className="text-sm text-zinc-500">
                {preview._facility_ids_count} facilities geselecteerd
              </span>
            )}
          </div>

          {preview._total_docs === 0 ? (
            <div className="text-sm text-zinc-500 italic">Geen documenten gevonden voor deze selectie.</div>
          ) : (
            <>
              <table className="w-full text-sm border border-zinc-200 mb-4">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-zinc-500 text-xs uppercase">Collectie</th>
                    <th className="px-3 py-2 text-right font-medium text-zinc-500 text-xs uppercase">Aantal docs</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview).filter(([k]) => !k.startsWith('_'))
                    .sort((a, b) => b[1] - a[1])
                    .map(([col, n]) => (
                      <tr key={col} className="border-t border-zinc-100">
                        <td className="px-3 py-1.5 font-mono text-xs">{col}</td>
                        <td className="px-3 py-1.5 text-right font-medium">{n.toLocaleString('nl-NL')}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-900 mb-4">
                <strong>Waarschuwing:</strong> deze actie verplaatst data permanent van <strong>{fromOpts.name}</strong>
                {' '}naar <strong>{toOpts.name}</strong>.
              </div>

              <label className="flex items-start gap-2 mb-4 p-3 border border-zinc-200 rounded-sm bg-zinc-50 cursor-pointer hover:bg-zinc-100 transition-colors" data-testid="move-auto-backup-label">
                <input
                  type="checkbox"
                  checked={autoBackup}
                  onChange={(e) => setAutoBackup(e.target.checked)}
                  className="mt-0.5"
                  data-testid="move-auto-backup-toggle"
                />
                <div>
                  <div className="text-sm font-medium text-zinc-800">
                    Maak automatisch een safety-backup vóór de move
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    Aanbevolen — duurt enkele seconden extra. Bij een verkeerde move kun je
                    via <em>Beveiliging → Backups</em> direct herstellen.
                  </div>
                </div>
              </label>

              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs uppercase tracking-wider text-zinc-500 mb-1">
                    Typ <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-700 font-bold">MOVE</code> ter bevestiging
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="MOVE"
                    className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm font-mono"
                    data-testid="move-confirm-input"
                  />
                </div>
                <button
                  onClick={runExecute}
                  disabled={confirmText !== 'MOVE' || executing}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="move-execute-btn"
                >
                  <ArrowsLeftRight size={14} weight="bold" />
                  {executing ? 'Bezig...' : 'Verplaatsen'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h2 className="font-medium text-zinc-900 text-sm">Geschiedenis (laatste 20)</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-2 text-left font-medium">Wanneer</th>
                <th className="px-5 py-2 text-left font-medium">Van → Naar</th>
                <th className="px-5 py-2 text-left font-medium">Mode</th>
                <th className="px-5 py-2 text-right font-medium">Docs</th>
                <th className="px-5 py-2 text-left font-medium">Door</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="px-5 py-2 text-zinc-600">{new Date(h.executed_at).toLocaleString('nl-NL')}</td>
                  <td className="px-5 py-2 text-zinc-700">{h.from_profile} → {h.to_profile}</td>
                  <td className="px-5 py-2 text-zinc-500 font-mono">{h.filters?.mode}</td>
                  <td className="px-5 py-2 text-right font-medium text-zinc-700">{h.total_docs?.toLocaleString('nl-NL')}</td>
                  <td className="px-5 py-2 text-zinc-500 truncate max-w-[200px]">{h.executed_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
